import { createClient } from '@supabase/supabase-js'
import type { Database } from './types'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY
  ?? import.meta.env.VITE_SUPABASE_KEY) as string;

export const resolveSupabaseProjectId = (
  explicitProjectId?: string,
  supabaseUrl?: string
) => {
  if (explicitProjectId) return explicitProjectId;
  try {
    return new URL(supabaseUrl ?? '').hostname.split('.')[0];
  } catch {
    return 'unknown-project';
  }
};

// Keep this centralized so Playwright storageState + runtime auth hydration use the exact same key
const SUPABASE_PROJECT_ID = resolveSupabaseProjectId(
  import.meta.env.VITE_SUPABASE_PROJECT_ID,
  SUPABASE_URL
);
export const SUPABASE_AUTH_TOKEN_KEY = `sb-${SUPABASE_PROJECT_ID}-auth-token`;

// Export for external use
export { SUPABASE_URL }

// Cookie-based auth storage for enhanced security
export const shouldUseSecureCookies = () => {
  // SECURITY FIX (CWE-922): Always use secure cookie-only storage in production/test
  // to prevent XSS token theft via localStorage. Only allow localStorage on explicit
  // local development with VITE_ALLOW_LOCALSTORAGE=true.
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    // Test mode: always secure
    if (import.meta.env.MODE === 'test') {
      return true;
    }
    if (import.meta.env.PROD && import.meta.env.VITE_ALLOW_LOCALSTORAGE === 'true') {
      throw new Error('VITE_ALLOW_LOCALSTORAGE must be disabled in production builds.');
    }
    // Explicit secure cookies flag
    if (import.meta.env.VITE_FORCE_SECURE_COOKIES === 'true') {
      return true;
    }
    // Production: always secure regardless of protocol check
    if (import.meta.env.PROD) {
      return true;
    }
    // Development: allow localStorage only if explicitly opted in
    if (import.meta.env.DEV && import.meta.env.VITE_ALLOW_LOCALSTORAGE === 'true') {
      return typeof window !== 'undefined' && window.location.protocol !== 'https:';
    }
  }
  // Default: secure cookies if HTTPS, or always secure in non-browser contexts
  return typeof window === 'undefined' || window.location.protocol === 'https:';
}

const getCookieDomainAttribute = () => {
  try {
    const host = window.location.hostname;

    // Never set Domain on localhost/dev hosts
    if (host === 'localhost' || host.endsWith('.local')) return '';

    // Share auth across tryoneform.com + subdomains (www/app/submit)
    if (host === 'tryoneform.com' || host.endsWith('.tryoneform.com')) {
      return '; Domain=.tryoneform.com';
    }

    return '';
  } catch {
    return '';
  }
};

/**
 * SECURE Storage (CWE-922 Fix):
 * - On HTTPS: Use cookies ONLY (no localStorage to prevent XSS token theft)
 * - On HTTP (dev): Use localStorage as fallback
 * This fixes cross-subdomain auth loss (www/app/submit) while eliminating XSS attack surface.
 */
export const supabaseAuthStorage = {
  getItem: (key: string) => {
    try {
      const cookies = document.cookie.split('; ').filter(Boolean);

      const getCookieRawValue = (cookiePair: string) => {
        const equalIndex = cookiePair.indexOf('=');
        const encodedValue = equalIndex >= 0 ? cookiePair.substring(equalIndex + 1) : '';
        return encodedValue || null;
      };

      const decodeCookieValue = (raw: string | null) => {
        if (!raw) return null;
        try {
          return decodeURIComponent(raw);
        } catch {
          // If the value isn't percent-encoded (or is malformed), return raw.
          return raw;
        }
      };

      const looksLikeSessionJson = (raw: string | null) => {
        if (!raw) return false;
        try {
          const parsed = JSON.parse(raw);
          return typeof parsed?.access_token === 'string' && parsed.access_token.length > 0;
        } catch {
          return false;
        }
      };

      // 1) Prefer a valid single-cookie value for this key
      const directMatches = cookies.filter((c) => c.startsWith(`${key}=`));
      for (const cookiePair of directMatches) {
        const raw = getCookieRawValue(cookiePair);
        const decoded = decodeCookieValue(raw);

        if (key === SUPABASE_AUTH_TOKEN_KEY) {
          if (decoded && looksLikeSessionJson(decoded)) return decoded;
        } else {
          if (decoded) return decoded;
        }
      }

      // 2) Chunked cookies fallback (cookie-size-safe)
      // Cookies: key.0, key.1, ...
      const chunkMatches = cookies
        .map((c) => {
          const equalIndex = c.indexOf('=');
          const name = equalIndex >= 0 ? c.substring(0, equalIndex) : c;
          const rawValue = getCookieRawValue(c);
          if (!name.startsWith(`${key}.`)) return null;
          const suffix = name.substring(key.length + 1);
          if (!/^[0-9]+$/.test(suffix)) return null;
          const index = Number(suffix);
          return Number.isFinite(index) && rawValue != null ? { index, value: rawValue } : null;
        })
        .filter((x): x is { index: number; value: string } => !!x)
        .sort((a, b) => a.index - b.index);

      if (chunkMatches.length > 0) {
        // IMPORTANT: join encoded chunks, then decode ONCE (chunk boundaries can split %XX)
        const encodedJoined = chunkMatches.map((p) => p.value).join('');
        const decoded = decodeCookieValue(encodedJoined);

        if (key === SUPABASE_AUTH_TOKEN_KEY) {
          if (decoded && looksLikeSessionJson(decoded)) return decoded;
        } else {
          if (decoded) return decoded;
        }
      }

      // 3) Read-only localStorage fallback for legacy recovery (https migration)
      try {
        const lsValue = localStorage.getItem(key);
        if (lsValue && (key !== SUPABASE_AUTH_TOKEN_KEY || looksLikeSessionJson(lsValue))) {
          console.debug('[Supabase Storage] Recovered value from localStorage fallback');
          return lsValue;
        }
      } catch {
        // localStorage may not be available
      }

      return null;
    } catch {
      return null;
    }
  },

  setItem: (key: string, value: string) => {
    const isSecure = shouldUseSecureCookies();
    const secure = isSecure ? '; Secure' : '';
    const domain = getCookieDomainAttribute();

    const setCookieEncoded = (cookieKey: string, encodedValue: string) => {
      // encodedValue MUST already be safe for cookies (no ';' / whitespace)
      document.cookie = `${cookieKey}=${encodedValue}; Path=/; SameSite=Lax; Max-Age=604800${secure}${domain}`;
    };

    // Cleanup any previous chunk cookies for this key to avoid stale fragments.
    try {
      const cookieNames = document.cookie.split('; ').map((c) => c.split('=')[0]).filter(Boolean);
      for (const name of cookieNames) {
        if (name === key || name.startsWith(`${key}.`)) {
          document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; SameSite=Lax${secure}`;
          if (domain) {
            document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; SameSite=Lax${secure}${domain}`;
          }
        }
      }
    } catch {
      // ignore
    }

    // Encode ONCE, then chunk the encoded string.
    // This prevents cookie truncation due to encodeURIComponent expansion.
    const encoded = encodeURIComponent(value);

    // Keep below typical 4096 cookie limit; name + attrs also take space.
    const ENCODED_CHUNK_SIZE = 3800;

    if (encoded.length > ENCODED_CHUNK_SIZE) {
      const parts: string[] = [];
      for (let i = 0; i < encoded.length; i += ENCODED_CHUNK_SIZE) {
        parts.push(encoded.slice(i, i + ENCODED_CHUNK_SIZE));
      }

      console.debug('[Supabase Storage] Storing chunked value', {
        key,
        parts: parts.length,
        bytes: value.length,
        encodedBytes: encoded.length,
      });

      parts.forEach((part, idx) => setCookieEncoded(`${key}.${idx}`, part));
    } else {
      setCookieEncoded(key, encoded);
    }

    // SECURITY FIX (CWE-922): Only write to localStorage on HTTP (dev environments)
    if (!isSecure) {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        console.warn('[Supabase Storage] localStorage write failed:', error);
      }
    }
  },

  removeItem: (key: string) => {
    const secure = shouldUseSecureCookies() ? '; Secure' : '';
    const domain = getCookieDomainAttribute();

    const expireCookie = (cookieKey: string) => {
      document.cookie = `${cookieKey}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; SameSite=Lax${secure}`;
      if (domain) {
        document.cookie = `${cookieKey}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; SameSite=Lax${secure}${domain}`;
      }
    };

    // Remove base cookie + any chunk cookies
    try {
      const cookieNames = document.cookie.split('; ').map((c) => c.split('=')[0]).filter(Boolean);
      for (const name of cookieNames) {
        if (name === key || name.startsWith(`${key}.`)) {
          expireCookie(name);
        }
      }
    } catch {
      expireCookie(key);
    }

    // Always clean up localStorage (legacy)
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  }
}

// Validate cookie storage functionality
function validateCookieStorage(): boolean {
  try {
    const testKey = 'sb-storage-test'
    const testValue = 'test-value=with-equals'

    supabaseAuthStorage.setItem(testKey, testValue)
    const retrieved = supabaseAuthStorage.getItem(testKey)
    supabaseAuthStorage.removeItem(testKey)

    if (retrieved !== testValue) {
      console.error('[Supabase Client] Cookie storage validation failed!', {
        expected: testValue,
        got: retrieved
      })
      return false
    }

    return true
  } catch (error) {
    console.error('[Supabase Client] Cookie storage validation error:', error)
    return false
  }
}

// Storage factory
const createStorage = () => {
  // Prefer hybrid storage on https; fall back to localStorage only on http.
  if (shouldUseSecureCookies()) {
    // Even if cookie sync is imperfect, localStorage fallback keeps auth stable.
    if (validateCookieStorage()) return supabaseAuthStorage
    return supabaseAuthStorage
  }

  // http dev environments
  return {
    getItem: (key: string) => {
      try {
        return localStorage.getItem(key)
      } catch {
        return null
      }
    },
    setItem: (key: string, value: string) => {
      try {
        localStorage.setItem(key, value)
      } catch (error) {
        console.warn('[Supabase Storage] localStorage write failed:', error)
      }
    },
    removeItem: (key: string) => {
      try {
        localStorage.removeItem(key)
      } catch {
        // Ignore errors
      }
    }
  }
}

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: createStorage(),
    storageKey: SUPABASE_AUTH_TOKEN_KEY,
  },
})
